const API_URL = import.meta.env.VITE_API_URL || (
  window.location.hostname === 'localhost'
    ? 'http://localhost:3000'
    : 'https://linguapals-backend.onrender.com'
);

/**
 * Decode the exp claim from a JWT without a library.
 * Returns expiry timestamp in milliseconds, or 0 on failure.
 */
function getTokenExpiry(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000;
  } catch {
    return 0;
  }
}

/**
 * Proactively refresh the access token if it expires within 60 seconds.
 * Uses the existing refreshAccessToken() lock — safe to call concurrently.
 */
async function ensureFreshToken() {
  const token = localStorage.getItem('access_token');
  if (!token) return;
  const expiresInMs = getTokenExpiry(token) - Date.now();
  if (expiresInMs < 60_000) {
    await refreshAccessToken();
  }
}

/**
 * Fetch wrapper with auth token injection and error handling.
 */
async function apiFetch(path, options = {}) {
  // Proactively refresh if token expires within 60 seconds
  if (!path.startsWith('/auth/')) {
    await ensureFreshToken();
  }
  const token = localStorage.getItem('access_token');

  // FormData bodies must NOT carry a manual Content-Type — the browser sets the
  // multipart boundary itself. JSON bodies keep the explicit header.
  const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
  const headers = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...options.headers,
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 150000);

  // If an external signal is provided, abort when it fires
  if (options?.signal) {
    if (options.signal.aborted) {
      controller.abort();
    } else {
      options.signal.addEventListener('abort', () => controller.abort(), { once: true });
    }
  }

  let res;
  try {
    res = await fetch(`${API_URL}${path}`, {
      ...options,
      headers,
      signal: controller.signal,
    });
  } catch (err) {
    clearTimeout(timeout);
    if (err.name === 'AbortError') throw new Error('Request timed out');
    throw err;
  }
  clearTimeout(timeout);

  if (res.status === 401) {
    if (path.startsWith('/auth/')) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      throw new Error('Session expired');
    }
    // Try to refresh token
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      // Retry with new token
      headers['Authorization'] = `Bearer ${localStorage.getItem('access_token')}`;
      const retryRes = await fetch(`${API_URL}${path}`, { ...options, headers });
      if (!retryRes.ok) {
        const err = await retryRes.json().catch(() => ({}));
        throw new Error(err.error?.message || `Request failed: ${retryRes.status}`);
      }
      return retryRes.status === 204 ? null : retryRes.json();
    }
    // Refresh failed — clear tokens, let React Router handle redirect
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    const { useAuthStore } = await import('../stores/authStore');
    useAuthStore.getState().logout().catch(() => {});
    throw new Error('Session expired');
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error?.message || `Request failed: ${res.status}`);
  }

  return res.status === 204 ? null : res.json();
}

let refreshPromise = null;

async function refreshAccessToken() {
  // If a refresh is already in flight, piggyback on it
  if (refreshPromise) return refreshPromise;

  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) return false;

  refreshPromise = (async () => {
    try {
      const res = await fetch(`${API_URL}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (!res.ok) return false;

      const data = await res.json();
      localStorage.setItem('access_token', data.access_token);
      localStorage.setItem('refresh_token', data.refresh_token);
      return true;
    } catch {
      return false;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

/**
 * Retry wrapper for GET requests — 1 retry after 1s delay.
 * POST/PUT/DELETE are never auto-retried (not idempotent).
 */
async function apiFetchWithRetry(path, options = {}) {
  try {
    return await apiFetch(path, options);
  } catch (err) {
    const method = (options.method || 'GET').toUpperCase();
    if (method !== 'GET') throw err;

    await new Promise((r) => setTimeout(r, 1000));
    return apiFetch(path, options);
  }
}

// Refresh token proactively when the tab comes back into focus
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      ensureFreshToken();
    }
  });
}

export const api = {
  get: (path) => apiFetchWithRetry(path),
  post: (path, body, opts) => apiFetch(path, {
    method: 'POST',
    body: JSON.stringify(body),
    ...opts,
  }),
  patch: (path, body) => apiFetch(path, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: (path) => apiFetch(path, { method: 'DELETE' }),
  // Multipart upload — pass a FormData; auth + token-refresh are inherited from
  // apiFetch, and the FormData branch above suppresses the JSON Content-Type.
  upload: (path, formData) => apiFetch(path, { method: 'POST', body: formData }),
};
